/*
 *  -----------------------------------------------------------------------
 *    <copyright file="edit-user-dialog/index.tsx" company="Triumph Research Intelligence">
 *        Copyright (C) 2019 by Triumph Research Intelligence. All rights reserved.
 *    </copyright>
 *   -----------------------------------------------------------------------
 *
 */
import { useMutation } from '@apollo/client/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { DialogContent } from '@mui/material';
import { Checkbox } from '@progress/kendo-react-inputs';
import React, { useEffect, useState } from 'react';

import { useBoolean, useTranslation } from 'hooks';
import { GET_RESTRICTED_STUDY, SAVE_SM_STUDY_MEMBER, SAVE_STUDY_MEMBER } from 'queries/studies';
import Button from 'views/components/core/buttons';
import CharactersRemaining from 'views/components/core/characters-remaining';
import { CustomDialog, DialogActions, DialogTitle } from 'views/components/core/dialog';
import ModalUpdatedInfo from 'views/components/core/modal-updated-info';
import TextField from 'views/components/core/text-field';

import './styles.scss';

const lengthLimit = 50;

interface EditUserDialogInterface {
    open: boolean;
    close(action?: string): any;
    user: any;
    studyId: any;
    email?: string;
    isSmMember?: boolean;
}

const EditUserDialog = ({ open, close, user, studyId, email, isSmMember }: EditUserDialogInterface) => {
    const { t } = useTranslation();
    const [saveStudyMember] = useMutation(isSmMember ? SAVE_SM_STUDY_MEMBER : SAVE_STUDY_MEMBER);
    const defaultState = {
        firstName: '',
        lastName: '',
        active: true,
        isAdmin: false,
        userId: null as any,
        isOpraUser: false,
    };
    const [state, setState] = useState({ ...defaultState });
    const [errors, setErrors] = useState({ firstName: '', lastName: '' });
    const disabledSaveBtn = useBoolean(true);

    useEffect(() => {
        if (!open) setState({ ...defaultState });
    }, [open]);

    useEffect(() => {
        if (user && open)
            setState({
                firstName: user.firstName,
                lastName: user.lastName,
                active: user.active,
                isAdmin: user.isAdmin,
                userId: user.userId,
                isOpraUser: !!user.user,
            });
    }, [user, open]);

    useEffect(() => {
        validation();
    }, [state]);

    const validation = (checkRequired?: boolean) => {
        let isValid = true;
        let firstNameError = '';
        let lastNameError = '';

        if (!state.firstName.length && checkRequired) {
            firstNameError = t('uam:enterFirstName');
            isValid = false;
        }

        if (!state.lastName.length && checkRequired) {
            lastNameError = t('uam:enterLastName');
            isValid = false;
        }

        if (state.firstName.length > lengthLimit) {
            firstNameError = `${t('limitExceededBy')} ${Math.abs(state.firstName.length - lengthLimit)} ${t(
                'characters',
            )}`;
            isValid = false;
        }
        if (state.lastName.length > lengthLimit) {
            lastNameError = `${t('limitExceededBy')} ${Math.abs(state.lastName.length - lengthLimit)} ${t(
                'characters',
            )}`;
            isValid = false;
        }
        setErrors({ firstName: firstNameError, lastName: lastNameError });
        return isValid;
    };

    const onClose = (action?: string) => {
        disabledSaveBtn.setTrue();
        close(action);
    };

    const submit = (event: any) => {
        event.preventDefault();
        disabledSaveBtn.setTrue();
        const studyMemberKey = isSmMember ? 'smStudyMember' : 'studyMember';

        if (user) {
            saveStudyMember({
                variables: {
                    studyId,
                    [studyMemberKey]: {
                        id: user.id,
                        firstName: user.firstName,
                        lastName: user.lastName,
                        active: state.active,
                        isAdmin: state.isAdmin,
                        email: user.email,
                        userId: user.userId,
                    },
                },
                refetchQueries: [
                    {
                        query: GET_RESTRICTED_STUDY,
                        variables: { id: studyId },
                    },
                ],
            })
                .then(() => {
                    onClose('edited');
                })
                .catch(() => {
                    onClose('error');
                });
        } else if (validation(true)) {
            saveStudyMember({
                variables: {
                    studyId,
                    [studyMemberKey]: {
                        id: null,
                        firstName: state.firstName,
                        lastName: state.lastName,
                        active: state.active,
                        isAdmin: state.isAdmin,
                        email: email || null,
                    },
                },
                refetchQueries: [
                    {
                        query: GET_RESTRICTED_STUDY,
                        variables: { id: studyId },
                    },
                ],
            })
                .then(() => {
                    onClose('assigned');
                })
                .catch(() => {
                    onClose('error');
                });
        }
    };

    return (
        <CustomDialog
            onClose={() => {
                onClose();
            }}
            fullWidth
            maxWidth="md"
            scroll="paper"
            open={open}
            className="custom-width-popup"
        >
            <DialogTitle id="edit-user-modal-title">
                {user ? t('uam:editStudyMember', { email: user?.recordId }) : t('uam:addStudyMemberEmail', { email })}
                <FontAwesomeIcon
                    icon={['fal', 'times']}
                    className="fa-lg close-icon-cv"
                    onClick={() => {
                        onClose();
                    }}
                    data-testid="close-dialog"
                />
            </DialogTitle>
            <DialogContent className="edit-user-dialog-content">
                <div className="checkbox-wrapper">
                    <Checkbox
                        rounded={null}
                        className="no-border"
                        checked={state.active}
                        onChange={() => {
                            disabledSaveBtn.setFalse();
                            setState({ ...state, active: !state.active });
                        }}
                        label={t('active')}
                        data-testid="active-checkbox"
                        size="medium"
                    />
                    {state.isOpraUser && (
                        <Checkbox
                            rounded={null}
                            className="no-border"
                            checked={state.isAdmin}
                            onChange={() => {
                                disabledSaveBtn.setFalse();
                                setState({ ...state, isAdmin: !state.isAdmin });
                            }}
                            label={t('uam:studyTeamAdministrator')}
                            data-testid="study-team-admin-checkbox"
                            size="medium"
                        />
                    )}
                </div>
                {user ? (
                    <>
                        <p className="main-content-p">
                            <span className="bold-text">{`${t('firstName')}: `}</span>
                            {user.firstName}
                        </p>
                        <p className="main-content-p">
                            <span className="bold-text">{`${t('lastName')}: `}</span>
                            {user.lastName}
                        </p>
                        <p className="main-content-p">
                            <span className="bold-text">{`${t('email')}: `}</span>
                            {user.email}
                        </p>
                    </>
                ) : (
                    <>
                        <TextField
                            label={`${t('firstName')}*`}
                            id="edit-user-first-name"
                            fullWidth
                            value={state.firstName}
                            onChange={(event: any) => {
                                disabledSaveBtn.setFalse();
                                setState({ ...state, firstName: event.target.value });
                            }}
                            placeholder={t('uam:enterFirstName')}
                            helperText={
                                <CharactersRemaining
                                    id="edit-user-first-name-help-text"
                                    length={state.firstName.length}
                                    max={lengthLimit}
                                    error={errors.firstName}
                                />
                            }
                            variant="outlined"
                            color="secondary"
                            className="before-select mb"
                            error={!!errors.firstName}
                        />
                        <TextField
                            label={`${t('lastName')}*`}
                            id="edit-user-last-name"
                            fullWidth
                            value={state.lastName}
                            onChange={(event: any) => {
                                disabledSaveBtn.setFalse();
                                setState({ ...state, lastName: event.target.value });
                            }}
                            placeholder={t('uam:enterLastName')}
                            helperText={
                                <CharactersRemaining
                                    id="edit-user-last-name-help-text"
                                    length={state.lastName.length}
                                    max={lengthLimit}
                                    error={errors.lastName}
                                />
                            }
                            variant="outlined"
                            color="secondary"
                            className="before-select mb"
                            error={!!errors.lastName}
                        />
                    </>
                )}

                <DialogActions className={`edit-user-dialog-content-actions ${!user ? 'flex-end' : ''}`}>
                    <ModalUpdatedInfo item={user} />
                    <Button
                        disabled={disabledSaveBtn.value || !!errors.firstName || !!errors.lastName}
                        className="edit-user-btn"
                        text={user ? 'update' : 'add'}
                        onClick={submit}
                    />
                </DialogActions>
            </DialogContent>
        </CustomDialog>
    );
};

export default EditUserDialog;
